import classNames from 'classnames'
import type { FunctionComponent } from 'react'
import { useMemo } from 'react'

import type { HeadingTag } from '../../components/heading'
import { Image } from '../../components/image'
import { Column, Container, Flex, Row } from '../../components/layout/grid'
import { Markdown } from '../../components/markdown'
import { Text } from '../../components/text'
import { TokenIcon } from '../../components/token-icon'
import { ActionButton } from '../../contentful-components/action-button'
import type { MultiCopyItemFragment } from '../../graphql/fragments/MultiCopyItemFragment.graphql'
import type { MultiCopySectionFragment } from '../../graphql/fragments/MultiCopySectionFragment.graphql'
import { getItemSpan } from '../../lib/utils/getItemSpan'
import { stripNonAlphaNumeric } from '../../lib/utils/text'
import styles from './multi-copy-section.module.scss'

const headingColors = ['future-fuscia', 'cyanora', 'coal-mine-canary']

export const MultiCopySection: FunctionComponent<Props> = ({
    heading,
    copy,
    itemsCollection,
    align = 'center',
    spacing = 'default',
    headingTag = 'h3',
}) => {
    const items = itemsCollection?.items ?? []
    const centered = align === 'center'
    const headingTypeStyle = centered
        ? { _: 'heading-md', md: 'heading-2xl' }
        : 'heading-sm'

    const itemSpan = useMemo(() => getItemSpan(items.length), [items.length])

    const maxItemHeadingLength = useMemo(() => {
        const maxLength = items.reduce(
            (max: number, item: MultiCopyItemFragment) => {
                const itemHeading = stripNonAlphaNumeric(item?.heading ?? '')
                return Math.max(max, itemHeading.length)
            },
            0
        )

        return maxLength
    }, [items])

    return (
        <Container
            className={classNames(
                styles['container'],
                centered && styles['center-text'],
                styles[`${spacing}-spacing`]
            )}
        >
            {!!heading && (
                <Flex
                    justify={centered ? 'center' : 'flex-start'}
                    className={classNames(
                        styles['heading-container'],
                        !!copy && styles['heading-container--with-copy']
                    )}
                >
                    <Markdown
                        markdown={heading}
                        tag={headingTag}
                        typeStyle={headingTypeStyle}
                    />
                </Flex>
            )}
            {!!copy && (
                <div
                    className={classNames(
                        styles['copy-container'],
                        !centered && styles['copy-container--left']
                    )}
                >
                    <Text tag='div' typeStyle='body-lg'>
                        {copy}
                    </Text>
                </div>
            )}
            <Row className={styles['item-row']}>
                {items.map(
                    (
                        { icon, heading, subheading, copy, button, image },
                        itemIndex
                    ) => (
                        <Column
                            className={classNames(styles['item-container'], {
                                [styles['item-container--centered']]: centered,
                            })}
                            span={itemSpan}
                            key={`${heading ?? subheading}--${itemIndex}`}
                        >
                            {image && (
                                <Image
                                    source={image.source}
                                    source2x={image.source2x}
                                    source3x={image.source3x}
                                    contentType={image.contentType}
                                    alt={image.alt}
                                    lazy
                                    className={styles.image}
                                />
                            )}
                            {!!icon && (
                                <div
                                    className={
                                        styles[
                                            centered
                                                ? 'token-container-large'
                                                : 'token-container-small'
                                        ]
                                    }
                                >
                                    <TokenIcon
                                        type={icon}
                                        scale={centered ? 1.75 : 1}
                                        className={styles['token-anchor']}
                                        color='primary'
                                    />
                                </div>
                            )}
                            <Flex justify={centered ? 'center' : 'flex-start'}>
                                <div className={styles['content-container']}>
                                    {!!heading && (
                                        <div className={styles['item-heading']}>
                                            <Markdown
                                                tag='h4'
                                                typeStyle={
                                                    maxItemHeadingLength > 30
                                                        ? 'heading-sm'
                                                        : 'heading-xl'
                                                }
                                                markdown={heading}
                                                color={
                                                    headingColors[
                                                        itemIndex %
                                                            headingColors.length
                                                    ]
                                                }
                                            />
                                        </div>
                                    )}
                                    {!!subheading && (
                                        <Markdown
                                            tag='h6'
                                            typeStyle={{
                                                _: 'body-lg',
                                                md: 'body-xl',
                                            }}
                                            color='text'
                                            markdown={`**${subheading}**`}
                                        />
                                    )}
                                    {!!copy && (
                                        <Markdown
                                            markdown={copy}
                                            typeStyle='body-lg'
                                            color='dim'
                                            boldColor='default'
                                            accentColor={
                                                heading && !subheading
                                                    ? headingColors[
                                                          itemIndex %
                                                              headingColors.length
                                                      ]
                                                    : 'primary'
                                            }
                                        />
                                    )}
                                </div>
                            </Flex>
                            {!!button && (
                                <div className={styles['button-container']}>
                                    <ActionButton {...button} />
                                </div>
                            )}
                        </Column>
                    )
                )}
            </Row>
        </Container>
    )
}

type Props = Omit<MultiCopySectionFragment, 'sys' | 'type'> & {
    headingTag?: HeadingTag
}

export default MultiCopySection
